<template>
  <div class="bui-tbl-topbar" :class="{ 'bui-tbl-topbar--compact': compactToolbar }">
    <TerSearchInput
      v-model="searchTerm"
      :placeholder="placeholder"
      class="search"
    />

    <slot name="after-search" />

    <TerButton
      v-if="hasFilters"
      ref="filterBtnRef"
      title="Filters"
      :aria-label="'Filters'"
      :active="filtersOpen"
      @click="onFiltersClick"
    >
      <template #icon><PhFunnelSimple :size="iconSize" weight="regular" /></template>
      <template v-if="!compactToolbar">Filters</template>
    </TerButton>

    <TerButton
      ref="columnsBtnRef"
      title="Columns"
      :aria-label="'Columns'"
      :active="columnsOpen"
      @click="toggleColumns"
    >
      <template #icon><PhColumns :size="iconSize" weight="regular" /></template>
    </TerButton>

    <TerButton
      v-if="canSwitchView"
      :title="`Switch to ${listViewType === 'table' ? 'grid' : 'table'} view`"
      :aria-label="`Switch view`"
      @click="toggleViewType"
    >
      <template #icon><PhDotsNine :size="iconSize" weight="regular" /></template>
    </TerButton>

    <TerButton
      :title="isLoading ? 'Refreshing…' : 'Refresh'"
      :aria-label="'Refresh'"
      @click="onRefreshClick"
    >
      <template #icon>
        <PhArrowsClockwise :size="iconSize" weight="regular" :class="{ spinning: isLoading }" />
      </template>
    </TerButton>

    <Teleport to="body">
      <template v-if="isMobile && sheetOpen">
        <div
          class="bui-tbl-sheet-backdrop"
          aria-hidden="true"
          @click="closeSheets"
        />
        <div
          class="bui-tbl-sheet"
          role="dialog"
          :aria-label="filtersOpen ? 'Filters' : 'Columns'"
        >
          <div class="bui-tbl-sheet__header">
            <h2 class="bui-tbl-sheet__title">{{ filtersOpen ? 'Filters' : 'Columns' }}</h2>
            <button type="button" class="bui-tbl-sheet__close" aria-label="Close" @click="closeSheets">
              <PhX :size="sheetCloseIconSize" weight="bold" />
            </button>
          </div>
          <div class="bui-tbl-sheet__body">
            <FiltersPaneContent v-if="filtersOpen" hide-search />
            <template v-else>
              <ul class="cols-list">
                <li v-for="col in columnsForToggle" :key="col.key" class="cols-item">
                  <label>
                    <input
                      type="checkbox"
                      :checked="!hiddenColumnNames.has(col.key)"
                      @change="onToggleColumn(col.key, ($event.target as HTMLInputElement).checked)"
                    />
                    <span>{{ col.label || col.key }}</span>
                  </label>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          v-if="filtersOpen && popoverRect"
          class="bui-tbl-popover bui-tbl-popover--filters"
          :style="popoverPosStyle('filters')"
          role="dialog"
          aria-label="Filters"
        >
          <FiltersPaneContent hide-search />
        </div>

        <div
          v-if="columnsOpen && popoverRect"
          class="bui-tbl-popover bui-tbl-popover--columns"
          :style="popoverPosStyle('columns')"
          role="dialog"
          aria-label="Columns"
        >
          <div class="popover-title">Columns</div>
          <ul class="cols-list">
            <li v-for="col in columnsForToggle" :key="col.key" class="cols-item">
              <label>
                <input
                  type="checkbox"
                  :checked="!hiddenColumnNames.has(col.key)"
                  @change="onToggleColumn(col.key, ($event.target as HTMLInputElement).checked)"
                />
                <span>{{ col.label || col.key }}</span>
              </label>
            </li>
          </ul>
        </div>
      </template>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  PhArrowsClockwise,
  PhColumns,
  PhDotsNine,
  PhFunnelSimple,
  PhX,
} from '@phosphor-icons/vue';
import TerSearchInput from '../../atoms/TerSearchInput/TerSearchInput.vue';
import TerButton from '../../atoms/TerButton/TerButton.vue';
import FiltersPaneContent from '@/components/ListingPages/ItemsLister/FiltersPane/FiltersPaneContent.vue';
import useIsMobile from '@/composables/utils/isMobile';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    hiddenColumns?: string[];
    columns?: Array<{ key: string; label?: string }>;
    /** BUI table path has no grid; hide dots-nine even if lister config enables grid. */
    showViewToggle?: boolean;
    /** Denser mobile lister: icon-only filter control, search on its own row. */
    compactToolbar?: boolean;
  }>(),
  {
    placeholder: 'Filter by Search...',
    hiddenColumns: () => [],
    columns: () => [],
    showViewToggle: false,
    compactToolbar: false,
  },
);

const emit = defineEmits<{
  'update:hiddenColumns': [value: string[]];
}>();

const itemsListHelpers = inject<any>('itemsListHelpers');
const listerConfig = inject<any>('listerConfig', {});

const isMobile = useIsMobile();

const iconSize = computed(() => {
  if (props.compactToolbar) return 17;
  return isMobile.value ? 18 : 24;
});
const sheetCloseIconSize = computed(() => (isMobile.value ? 18 : 22));

const searchTerm = computed({
  get: () => itemsListHelpers?.searchTerm?.value ?? '',
  set: (v: string) => {
    if (itemsListHelpers?.searchTerm) itemsListHelpers.searchTerm.value = v;
  },
});

const filtersConfig = computed(() => itemsListHelpers?.filtersConfig ?? []);
const columnsForToggle = computed(() => {
  if (props.columns.length) return props.columns;
  const apiCols = itemsListHelpers?.columnsConfig ?? [];
  return apiCols.map((c: any) => ({ key: c.name, label: c.label || c.name }));
});
const isLoading = computed(
  () => !!(itemsListHelpers?.isLoading?.value || itemsListHelpers?.isSWRValidating?.value),
);
const listViewType = computed({
  get: () => itemsListHelpers?.listViewType?.value ?? 'table',
  set: (v: 'table' | 'grid') => {
    if (itemsListHelpers?.listViewType) itemsListHelpers.listViewType.value = v;
  },
});

const canSwitchView = computed(
  () =>
    !!props.showViewToggle &&
    !!listerConfig?.availableListViewsConfig?.grid &&
    !!listerConfig?.availableListViewsConfig?.table,
);

const hasFilters = computed(() => Array.isArray(filtersConfig.value) && filtersConfig.value.length > 0);

const hiddenColumnNames = computed(() => new Set(props.hiddenColumns));

const filtersOpen = ref(false);
const columnsOpen = ref(false);

const filterBtnRef = ref<any>(null);
const columnsBtnRef = ref<any>(null);
const popoverRect = ref<DOMRect | null>(null);

const sheetOpen = computed(() => filtersOpen.value || columnsOpen.value);

function getBtnEl(ref: any): HTMLElement | null {
  return (ref?.value as any)?.$el ?? (ref as any) ?? null;
}

function measureFor(target: 'filters' | 'columns') {
  const refToUse = target === 'filters' ? filterBtnRef : columnsBtnRef;
  const el = getBtnEl(refToUse);
  if (el) popoverRect.value = el.getBoundingClientRect();
}

function popoverPosStyle(target: 'filters' | 'columns') {
  if (!popoverRect.value) return {};
  return {
    position: 'fixed' as const,
    top: `${popoverRect.value.bottom + 8}px`,
    right: `${window.innerWidth - popoverRect.value.right}px`,
    minWidth: target === 'filters' ? '320px' : '240px',
    maxWidth: '420px',
  };
}

function closeSheets() {
  filtersOpen.value = false;
  columnsOpen.value = false;
}

function onFiltersClick() {
  if (filtersOpen.value) {
    filtersOpen.value = false;
    return;
  }
  if (!isMobile.value) {
    measureFor('filters');
  } else {
    popoverRect.value = null;
  }
  filtersOpen.value = true;
  columnsOpen.value = false;
}

function toggleColumns() {
  if (columnsOpen.value) {
    columnsOpen.value = false;
    return;
  }
  if (!isMobile.value) {
    measureFor('columns');
  } else {
    popoverRect.value = null;
  }
  columnsOpen.value = true;
  filtersOpen.value = false;
}

function toggleViewType() {
  listViewType.value = listViewType.value === 'table' ? 'grid' : 'table';
}

function onRefreshClick() {
  itemsListHelpers?.reloadList?.();
}

function onToggleColumn(name: string, checked: boolean) {
  const next = new Set(hiddenColumnNames.value);
  if (checked) next.delete(name);
  else next.add(name);
  emit('update:hiddenColumns', Array.from(next));
}

function onDocClick(e: MouseEvent) {
  const t = e.target as Node | null;
  if (!t) return;

  if (isMobile.value && sheetOpen.value) {
    const el = t as Element;
    if (el.closest?.('.bui-tbl-sheet')) return;
    for (const r of [filterBtnRef.value, columnsBtnRef.value]) {
      const btn = (r as any)?.$el || (r as any);
      if (btn && btn.contains?.(t)) return;
    }
    closeSheets();
    return;
  }

  if (filtersOpen.value || columnsOpen.value) {
    const popovers = document.querySelectorAll('.bui-tbl-popover');
    for (const p of popovers) {
      if (p.contains(t)) return;
    }
    for (const r of [filterBtnRef.value, columnsBtnRef.value]) {
      const btn = (r as any)?.$el || (r as any);
      if (btn && btn.contains?.(t)) return;
    }
    closeSheets();
  }
}

function onWindowChange() {
  if (isMobile.value) return;
  if (filtersOpen.value) measureFor('filters');
  if (columnsOpen.value) measureFor('columns');
}

function onKeydownEscape(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  if (isMobile.value && sheetOpen.value) {
    e.preventDefault();
    closeSheets();
  }
}

watch(
  () => isMobile.value && sheetOpen.value,
  (active) => {
    if (active) {
      window.addEventListener('keydown', onKeydownEscape);
    } else {
      window.removeEventListener('keydown', onKeydownEscape);
    }
  },
);

watch([filtersOpen, columnsOpen], ([f, c]) => {
  if (isMobile.value) return;
  if (f) measureFor('filters');
  if (c) measureFor('columns');
});

onMounted(() => {
  document.addEventListener('click', onDocClick, true);
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true);
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
  window.removeEventListener('keydown', onKeydownEscape);
});
</script>

<style lang="scss" scoped>
.bui-tbl-topbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0;
  min-width: 0;
  flex-wrap: wrap;
}

.search {
  flex: 0 1 220px;
  min-width: 160px;
}

@media (max-width: 768px) {
  .bui-tbl-topbar {
    align-items: stretch;
    gap: 4px 6px;
  }

  .search {
    flex: 1 1 100%;
    min-width: 0;
    max-width: none;
  }

  /* Search first row, actions + bulk wrap on row below — less vertical clutter. */
  .bui-tbl-topbar--compact {
    align-content: flex-start;

    .search {
      order: -1;
    }
  }
}

.spinning {
  animation: bui-tbl-spin 1s linear infinite;
}

@keyframes bui-tbl-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

<style lang="scss">
.bui-tbl-popover {
  background: var(--bui-droplet-menu-fill, #fffcfd);
  color: var(--bui-droplet-menu-text, #2a2969);
  border-radius: var(--bui-droplet-menu-radius, 8px);
  box-shadow: 0 12px 40px rgba(75, 24, 134, 0.32);
  padding: 12px 16px;
  z-index: 9999;
  max-height: 70vh;
  overflow-y: auto;

  .popover-title {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
  }
}

.bui-tbl-popover--columns {
  .cols-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cols-item label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 4px;
    cursor: pointer;
    font-size: 14px;

    input[type='checkbox'] {
      accent-color: var(--bui-chk-checked-fill, #de3da9);
    }
  }
}

/* Reskin the legacy FiltersPaneContent controls (SelectExpandedField /
   Collapsible) onto BUI tokens, scoped to the BUI filters popover + mobile
   sheet so legacy list pages reusing these shared fields are unaffected. The
   leading `body ` wins specificity over the children's own scoped styles
   without needing !important. */
body .bui-tbl-popover--filters,
body .bui-tbl-sheet__body {
  .filter-option-label {
    color: var(--bui-tbl-detail-text, #5c5c66);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .filter-option-section.separated {
    border-top-color: var(--bui-tbl-line, #ffd9e6);
  }

  .options .option {
    color: var(--bui-tbl-detail-text, #5c5c66);

    &:hover {
      color: var(--bui-tbl-row-text, #2a2969);
    }
  }

  .options .option.selected {
    color: var(--bui-tbl-row-link, #4b1886);
    text-decoration-color: var(--bui-tbl-row-link, #4b1886);
  }

  .options .option:not(.vertical):not(:last-of-type)::after {
    background-color: var(--bui-tbl-line, #ffd9e6);
  }

  .collapsible .title {
    color: var(--bui-tbl-row-text, #2a2969);
  }

  .collapsible .collapsible-icon {
    color: var(--bui-tbl-head-fill, #a420c8);
  }

  .collapsible .collapsible-content {
    background-color: color-mix(in srgb, var(--bui-tbl-head-fill, #a420c8) 7%, transparent);
  }
}

.bui-tbl-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.45);
}

.bui-tbl-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: var(--bui-droplet-menu-fill, #fffcfd);
  color: var(--bui-droplet-menu-text, #2a2969);
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -8px 40px rgba(75, 24, 134, 0.28);
  overflow: hidden;
}

.bui-tbl-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px 6px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(42, 41, 105, 0.12);
}

.bui-tbl-sheet__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

.bui-tbl-sheet__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: rgba(42, 41, 105, 0.08);
  }
}

.bui-tbl-sheet__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 14px 20px;
  -webkit-overflow-scrolling: touch;

  .filters-pane {
    width: 100% !important;
    max-width: none;
  }

  .cols-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cols-item label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 4px;
    cursor: pointer;
    font-size: 14px;

    input[type='checkbox'] {
      accent-color: var(--bui-chk-checked-fill, #de3da9);
    }
  }
}

@media (max-width: 768px) {
  .bui-tbl-sheet {
    max-height: 82vh;
    border-radius: 10px 10px 0 0;
  }

  .bui-tbl-sheet__header {
    padding: 8px 12px 6px;
    gap: 8px;
  }

  .bui-tbl-sheet__title {
    font-size: 15px;
  }

  .bui-tbl-sheet__close {
    padding: 6px;
  }

  .bui-tbl-sheet__body {
    padding: 8px 12px 16px;
  }

  .bui-tbl-sheet__body .cols-item label {
    padding: 8px 2px;
    font-size: 13px;
  }
}
</style>
